import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import _ from 'lodash';
import classNames from 'classnames';

import ResultsStore from '../../../../stores/ResultsStore';

import DealActions from '../../../../actions/DealActions';
import HotelActions from '../../../../actions/HotelActions';

import Currencies from '../../../../utils/Currencies';
import RouterWrapper from '../../../../utils/RouterWrapper';

import DealPrice from '../../../DealPrice.react';
import NegotiatedRate from '../../../general/NegotiatedRate.react';

import DealUtils from '../../../../entities/DealUtils';

import { Tooltip, Button } from '@mui/material';
import Constants from '../deal/constants';

import Analytics from 'arbitrip-common/client/analytics';
import Config from 'arbitrip-common/client/utils/Config';
import ContentLoader from 'react-content-loader';
import ProfileStore from '../../../../stores/ProfileStore';
import { withRouter } from '../../../../utils/withRouter';
import PriceBreakdown from 'arbitrip-common/client/utils/PriceBreakdown';
import { Dates as DateUtils } from 'arbitrip-common/client/utils';

class RepresentativeDealComponent extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			travel_policy: ResultsStore.getTravelPolicy(),
		};
	}

	componentDidUpdate(prevProps) {
		if (prevProps.arbitripPointsApplied !== this.props.arbitripPointsApplied) {
			this.setState({ busy_applying_points: true });
			const _self = this;
			setTimeout(() => {
				_self.setState({ busy_applying_points: false });
			}, 1000);
		}
	}

	handleOnSelectClick = () => {
		const { profile, searchTerms, hotel, deal, searchToken } = this.props;

		HotelActions.getHotelData(hotel);
		if (deal) {
			DealActions.chooseDeal(deal);
		}

		const recommended_index = ResultsStore.calculateRecommendedIndex(hotel);
		HotelActions.setHotelRecommendedIndex(hotel.id, recommended_index);

		const recommended_count = ResultsStore.getRecommendedCount();
		const sort_method = ResultsStore.getSortMethod();
		const time_to_click_ml = ResultsStore.getTimePassedSinceSearchSessionCreatedInMilliSeconds();
		Analytics.actions.interactions.selectedHotel(
			profile,
			searchTerms.destination,
			searchTerms,
			recommended_index,
			recommended_count,
			sort_method,
			time_to_click_ml,
		);

		HotelActions.setHotelScroll(window.scrollY, hotel.id);
		window.open(RouterWrapper.buildHotelRoute(hotel, searchToken, searchTerms), '_blank');
	};

	getDealDisclaimerRender() {
		const { deal, t } = this.props;
		const doesIncludeTax = deal && deal.does_price_include_tax;
		const isPostPay = this.props.deal.post_pay;

		const tooltip_text = isPostPay ? t('PaymentHandledByProperty') : t('TotalPriceTooltipText');

		return (
			<Tooltip title={tooltip_text} placement="top" arrow>
				{isPostPay ? (
					<div className="tax-included">{t('PayAtHotel')}</div>
				) : doesIncludeTax ? (
					<div className="tax-included tooltip-icon">
						<p>{t('TaxIncluded')}</p>
					</div>
				) : (
					<div className="tax-not-included tooltip-icon">{t('MightNotIncludeTaxes')}</div>
				)}
			</Tooltip>
		);
	}

	render() {
		const { t, deal, profile, arbitripPointsApplied, contractClientCompanyId, hotel } = this.props;

		const faultDeal = deal && deal.faultDeal;
		const company_name = _.get(profile, 'company.name', null);

		const noDeal = !DealUtils.isValidDeal(deal);

		if (noDeal) {
			return null;
		}

		// We have a deal, continue with Render method ->

		const travelPolicy = this.state.travel_policy;
		const doesTravelPolicyExist = travelPolicy && (travelPolicy.default || travelPolicy.place);
		const isInPolicy = deal.in_policy;
		const travelPolicyCurrency = doesTravelPolicyExist ? Currencies.getSymbol(travelPolicy.currency) : '';
		const travelPolicyLocation =
			travelPolicy && travelPolicy.default
				? t('ThisDestination')
				: travelPolicy && travelPolicy.place
					? travelPolicy.place.city || travelPolicy.place.state || travelPolicy.place.country
					: '';
		const travelPolicyText = doesTravelPolicyExist
			? deal.out_of_policy_message ||
				t('Policy.OutOfPolicyMessage', {
					location: travelPolicyLocation,
					price: travelPolicyCurrency + travelPolicy.price,
				})
			: null;

		const tooltip = (
			<div className="policy-tooltip-container">
				<span>{travelPolicyText}</span>
			</div>
		);

		const previousPrice =
			Config.dev_mode && deal.previousPricePerNight
				? t('DevMode.OldPricePerNight', { price: deal.previousPricePerNight })
				: '';
		const originalCurrencyPrice =
			Config.dev_mode && deal.currency !== profile.display_currency
				? t('DevMode.OriginalCurrencyPrice', {
						currency: deal.currency,
						price: deal.pricePerNight,
					})
				: '';

		const arbitrip_points_reduction = arbitripPointsApplied ? _.get(profile, 'bank.balance', 0) : 0;

		let DEAL_PRICE = (
			<div className="deal-price-container">
				<div className="inner-container">
					{this.state.busy_applying_points ? (
						<ContentLoader width={124} height={32} viewBox="0 0 160 32">
							<rect x="0" y="0" rx="0" yx="0" width="160" height="32" />
						</ContentLoader>
					) : (
						<React.Fragment>
							{ProfileStore.hasArbitripPoints() && arbitripPointsApplied && (
								<div className="old-price">
									<span>
										{Currencies.getPriceWithDisplayCurrencyByPrecision(
											faultDeal ? deal.previousPricePerNight : deal.pricePerNight,
											profile.display_currency_exchange_rate,
											profile.display_currency,
											0,
										)}
									</span>
								</div>
							)}
							<div className="deal-price">
								<NegotiatedRate
									companyName={company_name}
									show={!!deal.negotiated_rate}
									rawCode={deal.original_negotiated_rate || ''}
									code={deal.negotiated_rate || ''}
									codeDisplayName={deal.negotiated_rate_display_name || ''}
								/>
								<div className="price-per-night">
									{PriceBreakdown.isBreakdownPrice({ profile, deal }) ? (
										<span data-testid="deal-price">
											{Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
												deal.expedia_pricing?.totalPrice,
												profile.display_currency_exchange_rate,
												profile.display_currency,
											)}
										</span>
									) : PriceBreakdown.isShowTotalPrice({ profile }) ? (
										<span data-testid="deal-price">
											{Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
												deal.totalPrice,
												profile.display_currency_exchange_rate,
												profile.display_currency,
											)}
										</span>
									) : (
										<>
											{profile.agent ? (
												<DealPrice
													previousPrice={previousPrice}
													displayTotalPrice={true}
													supplier={deal.supplier_name}
													currency={deal.currency}
													price={deal.pricePerNight}
													totalPrice={deal.totalPrice}
													netPrice={deal.net_price_total}
													commission={deal.commission_total}
													dealId={deal.id}
													faultDeal={faultDeal}
													profile={profile}
													contractClientCompanyId={contractClientCompanyId}
												/>
											) : (
												<span
													title={
														Config.dev_mode &&
														(previousPrice + (deal.currency !== profile.display_currency)
															? ' | ' + originalCurrencyPrice
															: '')
													}
													className={classNames(faultDeal && 'fault-deal')}
												>
													<span data-testid="deal-price">
														{Currencies.getPriceWithDisplayCurrencyByDefaultPrecision(
															(faultDeal
																? deal.previousPricePerNight
																: deal.pricePerNight) - arbitrip_points_reduction,
															profile.display_currency_exchange_rate,
															profile.display_currency,
														)}
													</span>
												</span>
											)}
										</>
									)}
								</div>
							</div>
						</React.Fragment>
					)}
				</div>
			</div>
		);

		const IS_IN_TRAVEL_POLICY = doesTravelPolicyExist && !PriceBreakdown.isBreakdownPrice({ profile, deal }) && (
			<Tooltip title={tooltip} placement="top-end" arrow>
				<div className="is-in-policy">
					<div className={isInPolicy ? 'in-policy icon' : 'out-of-policy icon'}></div>
					<div className={isInPolicy ? 'in-policy text' : 'out-of-policy text'}>
						{isInPolicy ? t('InPolicy') : t('OutOfPolicy')}
					</div>
				</div>
			</Tooltip>
		);

		const DEAL_ACTION = (
			<Button
				className="select-button tab-theme-color"
				onClick={this.handleOnSelectClick}
				variant="outline"
				disableRipple
				aria-label={`${t('Select')} ${hotel.name}`}
				role="link"
			>
				{t('Select')}
			</Button>
		);

		const checkIn = DateUtils.parseFormatDate(deal.check_in);
		const checkOut = DateUtils.parseFormatDate(deal.check_out);
		const nights = checkOut.diff(checkIn, 'days');

		return (
			<>
				<div
					className={
						PriceBreakdown.isBreakdownPrice({ profile, deal }) ? 'price-wrapper bottom' : 'price-wrapper'
					}
				>
					{DEAL_PRICE}
					{PriceBreakdown.isBreakdownPrice({ profile, deal }) ? (
						<div className="deal-price-per-night">
							{nights > 1 && (
								<div>
									{' '}
									<span className="price">
										{Currencies.getPriceWithDisplayCurrencyByPrecision(
											deal.expedia_pricing.totalPrice / nights,
											profile.display_currency_exchange_rate,
											profile.display_currency,
											0,
										)}
									</span>{' '}
									{t('AvgRoomNight')}
								</div>
							)}
							<div>{t('IncludesTaxesAndFees')}</div>
						</div>
					) : PriceBreakdown.isShowTotalPrice({ profile }) ? (
						<div className="deal-price-per-night">
							{nights > 1 && (
								<div>
									{' '}
									<span className="price">
										{Currencies.getPriceWithDisplayCurrencyByPrecision(
											deal.totalPrice / nights,
											profile.display_currency_exchange_rate,
											profile.display_currency,
											0,
										)}
									</span>{' '}
									{t('AvgRoomNight')}
								</div>
							)}
							{this.getDealDisclaimerRender()}
						</div>
					) : (
						<div className="per-night-text">{t('PerNight')}</div>
					)}
					{IS_IN_TRAVEL_POLICY}
				</div>
				{DEAL_ACTION}
			</>
		);
	}
}

RepresentativeDealComponent.propTypes = {
	deal: PropTypes.any,
	hotel: PropTypes.any,
	searchToken: PropTypes.any,
	searchTerms: PropTypes.any,
};

export default withTranslation()(withRouter(RepresentativeDealComponent));
